<template>
  <f7-page>
    <div v-if="loaded" class="custom-table-container tableContainer">
      <ServiceCatalog
        :store="servicesAnalytics"
        tab-key="analytics"
        @selector-initialized="onSelectorInitialized"
      />
    </div>
  </f7-page>
</template>

<script>
import ServiceCatalog from '@/components/ServiceCatalog/PacksAnalytics.vue';
import { mapState, mapActions } from 'vuex';
import { NOTIFY_TIME_LONG } from '@/js/constants';

const STORAGE_KEY = 'serviceCatalogLastSearch';
const DEFAULT_SAMPLE_TYPE_ID = 1;
const DEFAULT_SAMPLE_TYPE_SEARCH = 'agua';

export default {
  components: { ServiceCatalog },
  data() {
    return {
      loaded: false,
      selectedIndex: 0,
      selectedRoute: 0,
      priceAlert: false,
      familySelectors: {},
      sampleTypeSelectors: {},
      isRestoring: false,
    };
  },
  computed: {
    company() {
      return this.$store.state.contact.company;
    },
    ...mapState('serviceCatalog', [
      'servicesAnalytics',
      'serviceNoAnalytics',
      'serviceParameters',
      'idFamilySelected',
      'idTipoMuestra',
      'sampleTypes',
    ]),
  },
  watch: {
    async idFamilySelected(newVal) {
      this.syncFamilySelectors(newVal);

      if (this.isRestoring) {
        return;
      }

      try {
        this.$f7.preloader.show();

        this.setServicesAnalytics([]);
        this.setServiceParameters([]);
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7.preloader.hide();
      }
    },

    async idTipoMuestra(newVal) {
      this.syncSampleTypeSelectors(newVal);

      if (this.isRestoring) {
        return;
      }

      try {
        this.$f7.preloader.show();

        this.setServicesAnalytics([]);
        this.setServiceParameters([]);

        if (newVal == null) {
          this.saveLastSearch(this.idFamilySelected, null);
          return;
        }

        this.saveLastSearch(this.idFamilySelected, newVal);
        await this.fetchServices();
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7.preloader.hide();
      }
    },

    async company(newVal, oldVal) {
      if (newVal !== oldVal) {
        localStorage.removeItem(STORAGE_KEY);
        this.resetData();
      }
    },
  },

  async beforeMount() {
    try {
      this.$f7.preloader.show();
      await this.fetchServicesFamilies();
      const restored = await this.restoreLastSearch();

      if (!restored) {
        await this.initializeDefaultSearch();
      }
    } catch (error) {
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(error.message)
          ),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    } finally {
      this.$f7.preloader.hide();
      this.loaded = true;
    }
  },

  methods: {
    syncFamilySelectors(newVal) {
      Object.entries(this.familySelectors).forEach(([, selector]) => {
        if (selector && selector.option('value') !== newVal) {
          selector.option('value', newVal);
        }
      });
    },

    syncSampleTypeSelectors(newVal) {
      Object.entries(this.sampleTypeSelectors).forEach(
        ([, selector]) => {
          if (!selector || selector.option('value') === newVal) {
            return;
          }

          if (newVal == null) {
            selector.option('value', null);
            return;
          }

          const itemIds = this.sampleTypes.map(
            (item) => item.idTipoMuestra
          );

          if (itemIds.includes(newVal)) {
            selector.option('value', newVal);
          }
        }
      );
    },

    saveLastSearch(idFamilySelected, idTipoMuestra) {
      const extendedState = {
        idFamilySelected: idFamilySelected ?? null,
        idTipoMuestra: idTipoMuestra ?? null,
      };
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(extendedState)
      );
    },

    async restoreLastSearch() {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;

      try {
        const saved = JSON.parse(raw);
        const hasFamily = saved?.idFamilySelected != null;
        const hasTipo = saved?.idTipoMuestra != null;

        if (!hasFamily || !hasTipo) return false;

        this.isRestoring = true;
        this.setIdFamilySelected(saved.idFamilySelected);
        this.setIdTipoMuestra(saved.idTipoMuestra);
        await this.fetchServices();
        return true;
      } catch (error) {
        console.warn(`${STORAGE_KEY} corrupto en localStorage`, error);
        return false;
      } finally {
        this.isRestoring = false;
      }
    },

    async initializeDefaultSearch() {
      this.isRestoring = true;

      try {
        this.setIdTipoMuestra(DEFAULT_SAMPLE_TYPE_ID);
        await this.fetchSampleTypes(DEFAULT_SAMPLE_TYPE_SEARCH);
        await this.fetchServices();
        this.saveLastSearch(
          this.idFamilySelected,
          DEFAULT_SAMPLE_TYPE_ID
        );
      } finally {
        this.isRestoring = false;
      }
    },

    onSelectorInitialized(selectorName, tabKey, instance) {
      if (selectorName === 'familySelector') {
        this.familySelectors[tabKey] = instance;
        instance.option('value', this.idFamilySelected);
      } else if (selectorName === 'sampleTypeSelector') {
        this.sampleTypeSelectors[tabKey] = instance;
        instance.option('value', this.idTipoMuestra);
      }
    },

    ...mapActions('serviceCatalog', [
      'fetchServicesFamilies',
      'fetchServices',
      'fetchServiceNoAnalytics',
      'fetchServicesCatalogParameters',
      'fetchSampleTypes',
      'setServicesAnalytics',
      'setServiceParameters',
      'resetData',
      'setIdFamilySelected',
      'setIdTipoMuestra',
    ]),
  },
};
</script>

<style lang="scss">
@import './ServiceCatalog.scss';
</style>
